import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Typography,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { generateAuditedClient } from "../audit/auditedClient";
import { getDataSources } from "../graphql/queries";
import { DataSource } from "../models/API";
import { useGlobal } from "../context/GlobalContext";

const client = generateAuditedClient();

interface ResourceSelectorProps {
  userId: number;
  onSelectionChange: (knowledgeIds: number[], dataSourceIds: number[]) => void;
}

const ResourceSelector: React.FC<ResourceSelectorProps> = ({
  userId,
  onSelectionChange,
}) => {
  const { globalTeamId, env } = useGlobal();
  const [expanded, setExpanded] = useState(false);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [isDataSourceLoaded, setIsDataSourceLoaded] = useState(false);

  const hasNotifiedParent = useRef(false);

  const getStorageKey = useCallback(
    () => `finn_datasource_selection_${userId}_${globalTeamId}_${env}`,
    [userId, globalTeamId, env]
  );

  const [selectedDataSourceIds, setSelectedDataSourceIds] = useState<number[]>(
    () => {
      try {
        const stored = localStorage.getItem(
          `finn_datasource_selection_${userId}_${globalTeamId}_${env}`
        );
        return stored ? JSON.parse(stored) : [];
      } catch {
        return [];
      }
    }
  );

  const loadDataSources = useCallback(async () => {
    try {
      const result = await client.graphql({
        query: getDataSources,
        variables: { input: { user_id: userId, team_id: globalTeamId } },
      });
      const dss = result.data.getDataSources || [];
      setDataSources(dss);

      const storedKey = getStorageKey();
      const hasStoredSelection = localStorage.getItem(storedKey) !== null;

      if (!hasStoredSelection && dss.length > 0) {
        const allIds = dss.map((ds) => ds.id);
        setSelectedDataSourceIds(allIds);
      } else {
        const validIds = dss.map((ds) => ds.id);
        setSelectedDataSourceIds((prev) =>
          prev.filter((id) => validIds.includes(id))
        );
      }

      setIsDataSourceLoaded(true);
    } catch (error) {
      console.error("Error loading data sources:", error);
      setIsDataSourceLoaded(true);
    }
  }, [userId, globalTeamId, getStorageKey]);

  useEffect(() => {
    if (userId > 0) {
      setIsDataSourceLoaded(false);
      hasNotifiedParent.current = false;
      loadDataSources();
    }
  }, [userId, loadDataSources]);

  useEffect(() => {
    if (isDataSourceLoaded) {
      localStorage.setItem(
        getStorageKey(),
        JSON.stringify(selectedDataSourceIds)
      );
    }
  }, [selectedDataSourceIds, getStorageKey, isDataSourceLoaded]);

  useEffect(() => {
    if (isDataSourceLoaded && !hasNotifiedParent.current) {
      onSelectionChange([], selectedDataSourceIds);
      hasNotifiedParent.current = true;
    } else if (isDataSourceLoaded) {
      onSelectionChange([], selectedDataSourceIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataSourceIds, isDataSourceLoaded]);

  const handleDataSourceChange = (event: any) => {
    const value = event.target.value;
    setSelectedDataSourceIds(value);
  };

  return (
    <Box
      sx={{
        p: 0.75,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        backgroundColor: "#fafafa",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setExpanded(!expanded)}
        sx={{
          cursor: "pointer",
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
          borderRadius: 1,
          p: 0.5,
        }}
      >
        <Typography variant="caption" color="textSecondary">
          {selectedDataSourceIds.length > 0
            ? `${selectedDataSourceIds.length} data sources selected`
            : "Select data sources (optional)"}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box mt={1}>
          <FormControl size="small" sx={{ minWidth: 180, width: "100%" }}>
            <InputLabel sx={{ fontSize: "0.875rem" }}>Data Sources</InputLabel>
            <Select
              multiple
              value={selectedDataSourceIds}
              onChange={handleDataSourceChange}
              input={<OutlinedInput label="Data Sources" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => {
                    const ds = dataSources.find((d) => d.id === value);
                    return (
                      <Chip
                        key={value}
                        label={ds?.display_name || value}
                        size="small"
                        sx={{ height: "20px", fontSize: "0.75rem" }}
                      />
                    );
                  })}
                </Box>
              )}
              sx={{ fontSize: "0.875rem" }}
            >
              {dataSources.length === 0 ? (
                <MenuItem disabled>No data sources available</MenuItem>
              ) : (
                dataSources.map((ds) => (
                  <MenuItem key={ds.id} value={ds.id}>
                    {ds.display_name} ({ds.data_source_type})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ResourceSelector;
